import { Link } from 'react-router-dom'
import { PageTemplate } from '@/components/PageTemplate'
import { NavLink } from '@/components/NavLink'
import { track } from '@/lib/analytics'
import { AI_NOW, CASES_COPY } from '@/data/cases'
import { CASE_STUDIES, type CaseStudy } from '@/data/case-studies'

/**
 * /keysy — хаб кейсов (ru). С 2026-10-01 у каждого кейса своя страница
 * /keysy/<slug> в едином формате (src/pages/case-study.tsx), а хаб — их
 * оглавление: карточка в одном формате у всех кейсов, ссылка на разбор.
 *
 * Якоря карточек (#cosmetics, #novavi…) сохранены: ими ведут старые ссылки
 * с главной и из постов — человек попадает на карточку нужного кейса.
 * ИИ-проекты в работе своих страниц не имеют: под NDA у них только сектор и
 * масштаб, отдельная страница была бы пустой — они остаются блоком здесь.
 */

const URL = 'https://dnai.engineering/keysy'

const TITLE = 'Кейсы внедрения ИИ и масштабирования бизнеса | DNAI'
const DESCRIPTION =
  'Кейсы внедрения ИИ и масштабирования бизнеса с цифрами: точка А, что сделали, точка Б, что не получилось, выводы для собственника. Четыре ИИ-кейса и шесть проектов роста.'

const t = CASES_COPY.ru

const LIVE = CASE_STUDIES.filter((c) => c.kind === 'live-ai')
const PRE_AI = CASE_STUDIES.filter((c) => c.kind === 'pre-ai')

const SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Кейсы внедрения ИИ и масштабирования бизнеса — DNAI Engineering',
    description: DESCRIPTION,
    url: URL,
    inLanguage: 'ru-RU',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: CASE_STUDIES.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${URL}/${c.slug}`,
        name: c.ru.h1,
      })),
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://dnai.engineering/' },
      { '@type': 'ListItem', position: 2, name: 'Кейсы', item: URL },
    ],
  },
]

const LABEL = 'text-small uppercase tracking-wider text-muted'
const ANCHOR = 'text-accent underline underline-offset-4 decoration-accent/40 hover:decoration-accent'

/** Карточка кейса — один формат для всех: тип · отрасль, заголовок разбора, две цифры. */
function CaseCard({ cs }: { cs: CaseStudy }) {
  const c = cs.ru
  return (
    <article id={cs.id} className="scroll-mt-24">
      <Link
        to={`/keysy/${cs.slug}`}
        onClick={() => track('case_open', { case: cs.id })}
        className="block h-full border border-divider rounded-card p-6 sm:p-8 no-underline hover:border-accent transition-colors"
      >
        <div className={LABEL}>{c.sector}</div>
        <h3 className="mt-3 text-h3 text-ink font-medium leading-snug">{c.h1}</h3>
        <ul className="mt-6 grid gap-4 sm:grid-cols-2">
          {c.figures.slice(0, 2).map((f) => (
            <li key={f.label}>
              <div className="font-serif text-h2 text-accent tabular-nums leading-none">{f.value}</div>
              <div className="mt-2 text-small text-muted leading-snug">{f.label}</div>
            </li>
          ))}
        </ul>
        <span className="mt-6 inline-flex items-center gap-2 text-small font-medium text-accent">
          Читать кейс <span aria-hidden>→</span>
        </span>
      </Link>
    </article>
  )
}

export default function KeysyPage() {
  return (
    <PageTemplate title={TITLE} description={DESCRIPTION} canonical={URL} schemaJsonLd={SCHEMA}>
      <article className="section">
        <div className="container-x max-w-prose">
          <nav aria-label="Хлебные крошки" className="text-small text-muted mb-6">
            <NavLink href="/" className="text-muted hover:text-ink no-underline">
              Главная
            </NavLink>
            <span aria-hidden> / </span>
            <span>Кейсы</span>
          </nav>

          <div className="text-small uppercase tracking-wider text-accent mb-4 font-medium">Кейсы</div>
          <h1 className="text-h1 text-ink leading-tight font-serif">
            Кейсы внедрения ИИ и масштабирования бизнеса
          </h1>
          <p className="mt-8 text-body text-muted leading-relaxed">
            Здесь собраны все наши кейсы. У каждого — своя страница в одном формате: точка А, что
            сделали, точка Б в цифрах, что не получилось и что взять себе. Четыре первых — живые
            внедрения ИИ: публикуем их с середины проекта. Шесть следующих — масштабирование бизнеса
            до эпохи ИИ: на той же методике мы строим ИИ-внедрения сегодня. У трёх именованных кейсов
            есть видео-отзыв собственника. ИИ-проекты идут под соглашением о неразглашении, поэтому в
            них названы только сектор и масштаб — без имён и опознаваемых деталей.
          </p>
          {/* Строка сохранена из прежнего блока продукта (ТЗ 3: срезанное с
              главной переезжает сюда, а не исчезает). */}
          <p className="mt-4 text-small text-muted leading-relaxed">
            Подтверждённые сектора: транспорт (6 городов), энергетика и тендеры, девелопмент,
            финтех, e-commerce, производство. Имена закрыты соглашением о неразглашении — кейсы разбираем на созвоне.
          </p>

          <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-small">
            <li>
              <a href="#live" className={ANCHOR}>
                {t.liveKicker}
              </a>
            </li>
            <li>
              <a href="#do-epohi-ii" className={ANCHOR}>
                Кейсы до эпохи ИИ
              </a>
            </li>
            <li>
              <a href="#v-rabote" className={ANCHOR}>
                {t.aiKicker}
              </a>
            </li>
          </ul>

          {/* ——— Живые ИИ-кейсы ——— */}
          <section id="live" className="mt-16 scroll-mt-24">
            <div className="text-small uppercase tracking-wider text-accent mb-3">{t.liveKicker}</div>
            <h2 className="text-h2 text-ink">{t.liveHeading}</h2>
            <p className="mt-5 text-body text-muted">{t.liveIntro}</p>
            <div className="mt-10 grid gap-6">
              {LIVE.map((cs) => (
                <CaseCard key={cs.id} cs={cs} />
              ))}
            </div>
            <p className="mt-8 text-small text-muted">{t.liveDocFact}</p>
          </section>

          {/* ——— Кейсы до эпохи ИИ ——— */}
          <section id="do-epohi-ii" className="mt-20 pt-12 border-t border-divider scroll-mt-24">
            <div className="text-small uppercase tracking-wider text-accent mb-3">{t.kicker}</div>
            <h2 className="text-h2 text-ink">{t.heading}</h2>
            <p className="mt-6 text-body text-muted italic">{t.intro}</p>
            <div className="mt-10 grid gap-6">
              {PRE_AI.map((cs) => (
                <CaseCard key={cs.id} cs={cs} />
              ))}
            </div>
            <p className="mt-8 text-small text-muted">{t.liveBackground}</p>
          </section>

          {/* ——— ИИ-проекты в работе сейчас ——— */}
          <section id="v-rabote" className="mt-20 pt-12 border-t border-divider scroll-mt-24">
            <div className="text-small uppercase tracking-wider text-accent mb-3">{t.aiKicker}</div>
            <h2 className="text-h2 text-ink">{t.aiHeading}</h2>
            <p className="mt-6 text-body text-muted">{t.aiIntro}</p>

            {AI_NOW.map((c) => {
              const a = c.ru
              return (
                <article key={c.id} id={c.id} className="mt-10 pt-8 border-t border-divider scroll-mt-24">
                  <div className="flex items-start justify-between gap-4">
                    <div className={LABEL}>{a.sector}</div>
                    <span className="shrink-0 text-small text-accent border border-accent/40 rounded-btn px-2.5 py-0.5 whitespace-nowrap">
                      {a.status}
                    </span>
                  </div>
                  <h3 className="mt-3 text-h3 text-ink font-medium leading-snug">{a.scope}</h3>
                  <ul className="mt-5 space-y-2 text-body text-ink">
                    {a.facts.map((f) => (
                      <li key={f} className="flex gap-2">
                        <span className="text-accent mt-[0.35em] shrink-0" aria-hidden>
                          →
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              )
            })}
          </section>

          <div className="mt-16">
            <NavLink href="#final-cta" className="btn-primary no-underline">
              Записаться на диагностику
            </NavLink>
          </div>
        </div>
      </article>
    </PageTemplate>
  )
}
